import { Fragment, useMemo, useState } from "react";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { KpiCard, Tag, Tabs, Btn } from "../components/index.js";
import { Card } from "../components/Card.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { useAsync } from "../lib/useAsync.js";
import { members, hasRole, stores as storesApi, type MemberDetail } from "../lib/api.js";
import type { Role } from "@tebiki/shared";

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "owner", label: "owner — 組織のすべて" },
  { value: "admin", label: "admin — 管理（メンバー・店舗）" },
  { value: "editor", label: "editor — 作成・編集" },
  { value: "reviewer", label: "reviewer — 承認・レビュー" },
  { value: "viewer", label: "viewer — 閲覧のみ" },
];

/** Role → highlighter/structure-color treatment (owner/admin stand out; viewer is quiet). */
function roleTag(role: Role) {
  switch (role) {
    case "owner":
      return <Tag hi>owner</Tag>;
    case "admin":
      return <Tag color="var(--hi)">admin</Tag>;
    case "editor":
      return <Tag color="var(--ok)">editor</Tag>;
    case "reviewer":
      return <Tag>reviewer</Tag>;
    default:
      return (
        <Tag mono color="var(--muted)">
          viewer
        </Tag>
      );
  }
}

/** Display name (or email local-part) for a member row; falls back to the user id when unknown. */
function memberName(m: MemberDetail): string {
  if (m.user?.displayName) return m.user.displayName;
  if (m.user?.email) return m.user.email.split("@")[0];
  return m.membership.userId;
}

/** First grapheme of the display name for the avatar disc. */
function initial(name: string): string {
  return [...name][0]?.toUpperCase() ?? "?";
}

/**
 * Members & permissions — real org memberships joined with users + stores. Admin (owner/admin)
 * can add members (email + role + store scope + optional initial password) and edit each row
 * (名前 / ロール / パスワード) inline. No demo data.
 */
export function Members() {
  const { data, loading, error, offline, reload } = useAsync(() => members.listDetailed());
  const { data: storeList } = useAsync(() => storesApi.list());
  const [tab, setTab] = useState("members");
  const canAdmin = hasRole("owner", "admin");

  // ---- add member (admin) ----
  const [showInvite, setShowInvite] = useState(false);
  const [invEmail, setInvEmail] = useState("");
  const [invName, setInvName] = useState("");
  const [invRole, setInvRole] = useState<Role>("editor");
  const [invStore, setInvStore] = useState(""); // "" = 全店（組織全体）
  const [invPw, setInvPw] = useState("");
  const [invBusy, setInvBusy] = useState(false);
  const [invMsg, setInvMsg] = useState<string | null>(null);

  async function submitInvite() {
    const email = invEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setInvMsg("メールアドレスを確認してください。");
      return;
    }
    if (invPw && invPw.length < 8) {
      setInvMsg("初期パスワードは8文字以上にしてください。");
      return;
    }
    setInvBusy(true);
    setInvMsg(null);
    try {
      const res = await members.invite({
        email,
        displayName: invName.trim() || undefined,
        role: invRole,
        storeId: invStore || null,
      });
      if (invPw) await members.setPassword(res.user.id, invPw);
      setShowInvite(false);
      setInvEmail("");
      setInvName("");
      setInvPw("");
      setInvRole("editor");
      setInvStore("");
      reload();
    } catch {
      setInvMsg("追加に失敗しました。権限（owner/admin）と通信環境を確認してください。");
    } finally {
      setInvBusy(false);
    }
  }

  // ---- inline edit per membership row (名前 / ロール / パスワード) ----
  const [editFor, setEditFor] = useState<string | null>(null); // membership.id
  const [edName, setEdName] = useState("");
  const [edRole, setEdRole] = useState<Role>("editor");
  const [edPw, setEdPw] = useState("");
  const [edBusy, setEdBusy] = useState(false);
  const [edMsg, setEdMsg] = useState<string | null>(null);

  function openEdit(m: MemberDetail) {
    setEditFor(m.membership.id);
    setEdName(m.user?.displayName ?? "");
    setEdRole(m.membership.role);
    setEdPw("");
    setEdMsg(null);
  }

  async function saveEdit(m: MemberDetail) {
    const user = m.user;
    if (!user) return;
    const name = edName.trim();
    if (edPw && edPw.length < 8) {
      setEdMsg("パスワードは8文字以上にしてください。");
      return;
    }
    setEdBusy(true);
    setEdMsg(null);
    try {
      if (name && name !== (user.displayName ?? "")) await members.updateProfile(user.id, { displayName: name });
      if (edRole !== m.membership.role) await members.updateRole(user.id, m.membership.id, edRole);
      if (edPw) await members.setPassword(user.id, edPw);
      setEditFor(null);
      reload();
    } catch (e) {
      setEdMsg(
        String(e).includes("cannot_demote_last_owner")
          ? "最後の owner は降格できません。先に別のメンバーを owner にしてください。"
          : "保存に失敗しました。権限（owner/admin）と通信環境を確認してください。",
      );
    } finally {
      setEdBusy(false);
    }
  }

  const rows = useMemo(
    () =>
      (data ?? [])
        .slice()
        .sort((a, b) => memberName(a).localeCompare(memberName(b), "ja")),
    [data],
  );

  // Honest KPIs only — derived from the real list (no seats/dormant data exists server-side).
  const memberCount = rows.length;
  const roleCount = useMemo(
    () => new Set(rows.map((m) => m.membership.role)).size,
    [rows],
  );
  const storeCount = useMemo(
    () =>
      new Set(
        rows.map((m) => m.store?.id ?? m.membership.storeId).filter((s): s is string => !!s),
      ).size,
    [rows],
  );

  return (
    <ConsoleShell breadcrumb={["TEBIKI", "メンバー・権限"]}>
      <PageHead title={<>メンバー<span className="hl">・権限</span></>}>
        ロール（owner / admin / editor / reviewer / viewer）と店舗スコープを管理。
      </PageHead>

      <Tabs
        active={tab}
        onChange={setTab}
        ariaLabel="メンバービュー"
        tabs={[
          { id: "members", label: "メンバー" },
          { id: "roles", label: "ロール権限" },
        ]}
      />

      {tab === "roles" ? (
        <EmptyState icon={<Icon name="settings" size={30} />} title="ロール権限の編集は準備中です">
          owner / admin / editor / reviewer / viewer ごとの操作許可をここで細かく設定できるようにします。
          現在のロール割り当ては「メンバー」タブで確認できます。
        </EmptyState>
      ) : loading ? (
        <LoadingState label="メンバーを読み込み中…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : (
        <>
          {canAdmin ? (
            <div className="row" style={{ justifyContent: "flex-end", marginBottom: 14 }}>
              <Btn
                variant={showInvite ? "ghost" : "primary"}
                icon={<Icon name={showInvite ? "close" : "members"} size={15} />}
                aria-expanded={showInvite}
                onClick={() => {
                  setShowInvite((v) => !v);
                  setInvMsg(null);
                }}
              >
                {showInvite ? "閉じる" : "メンバーを追加"}
              </Btn>
            </div>
          ) : null}

          {canAdmin && showInvite ? (
            <Card crop pad className="reveal" style={{ marginBottom: 20 }}>
              <div className="kicker" style={{ marginBottom: 12 }}>メンバーを追加</div>
              <div className="grid g-2" style={{ gap: 14 }}>
                <div>
                  <label className="field-label" htmlFor="inv-email">メールアドレス（必須）</label>
                  <input
                    id="inv-email"
                    className="input"
                    type="email"
                    autoComplete="off"
                    value={invEmail}
                    onChange={(e) => setInvEmail(e.target.value)}
                    placeholder="taro@example.co.jp"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="inv-name">名前（任意）</label>
                  <input
                    id="inv-name"
                    className="input"
                    type="text"
                    autoComplete="off"
                    value={invName}
                    onChange={(e) => setInvName(e.target.value)}
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="inv-role">ロール</label>
                  <select id="inv-role" className="select" value={invRole} onChange={(e) => setInvRole(e.target.value as Role)}>
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="inv-store">店舗スコープ</label>
                  <select id="inv-store" className="select" value={invStore} onChange={(e) => setInvStore(e.target.value)}>
                    <option value="">全店（組織全体）</option>
                    {(storeList ?? []).map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="inv-pw">初期パスワード（任意・8文字以上）</label>
                  <input
                    id="inv-pw"
                    className="input"
                    type="text"
                    autoComplete="off"
                    value={invPw}
                    onChange={(e) => setInvPw(e.target.value)}
                    placeholder="空欄ならマジックリンクでログイン"
                  />
                </div>
              </div>
              {invMsg ? (
                <p className="jp" role="alert" style={{ margin: "12px 0 0", fontSize: 13, color: "var(--danger)" }}>
                  {invMsg}
                </p>
              ) : (
                <p className="jp" style={{ margin: "12px 0 0", fontSize: 12, color: "var(--muted)" }}>
                  パスワードを設定すると、メールアドレス＋パスワードですぐログインできます。空欄の場合はマジックリンク（メール）でのログインになります。
                </p>
              )}
              <div className="row" style={{ gap: 8, marginTop: 14 }}>
                <Btn variant="primary" onClick={() => void submitInvite()} disabled={invBusy}>
                  {invBusy ? "追加中…" : "追加する"}
                </Btn>
                <Btn variant="ghost" onClick={() => setShowInvite(false)} disabled={invBusy}>
                  キャンセル
                </Btn>
              </div>
            </Card>
          ) : null}

          {rows.length === 0 ? (
            <EmptyState icon={<Icon name="members" size={30} />} title="メンバーがまだいません">
              この組織に招待されたメンバーが、ロールと店舗スコープとともにここに表示されます。
            </EmptyState>
          ) : (
            <>
              <div className="grid g-3" style={{ marginBottom: 20 }}>
                <KpiCard label="メンバー" value={memberCount} suffix="人" className="reveal" />
                <KpiCard label="ロール種別" value={roleCount} className="reveal d1" />
                <KpiCard label="店舗" value={storeCount} className="reveal d2" />
              </div>

              <div className="list reveal d1">
                <div className="lrow lhead mcols" style={mcols}>
                  <span />
                  <span>名前</span>
                  <span>ロール</span>
                  <span>店舗</span>
                  <span />
                </div>
                {rows.map((m) => {
                  const name = memberName(m);
                  const rowId = m.membership.id;
                  const editing = editFor === rowId;
                  return (
                    <Fragment key={rowId}>
                      <div className="lrow mcols" style={mcols}>
                        <span className="avatar" aria-hidden="true">
                          {initial(name)}
                        </span>
                        <div>
                          <b className="jp">{name}</b>
                          <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
                            {m.user?.email ?? "メール未取得"}
                          </div>
                        </div>
                        {roleTag(m.membership.role)}
                        <span className="jp">
                          {m.store ? (
                            m.store.name
                          ) : m.membership.storeId ? (
                            <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                              店舗 ID: {m.membership.storeId.slice(0, 8)}
                            </span>
                          ) : (
                            <Tag mono>全店</Tag>
                          )}
                        </span>
                        <span style={{ justifySelf: "end" }}>
                          {canAdmin && m.user ? (
                            <button
                              type="button"
                              className="tag"
                              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}
                              aria-expanded={editing}
                              onClick={() => (editing ? setEditFor(null) : openEdit(m))}
                            >
                              <Icon name="settings" size={13} /> 編集
                            </button>
                          ) : null}
                        </span>
                      </div>
                      {canAdmin && editing ? (
                        <div style={{ padding: "14px 16px", borderTop: "1px dashed var(--line)", background: "var(--bg2)" }}>
                          <div className="grid g-3" style={{ gap: 14 }}>
                            <div>
                              <label className="field-label" htmlFor={`ed-name-${rowId}`}>名前</label>
                              <input
                                id={`ed-name-${rowId}`}
                                className="input"
                                type="text"
                                autoComplete="off"
                                value={edName}
                                onChange={(e) => setEdName(e.target.value)}
                                placeholder={name}
                              />
                            </div>
                            <div>
                              <label className="field-label" htmlFor={`ed-role-${rowId}`}>ロール</label>
                              <select id={`ed-role-${rowId}`} className="select" value={edRole} onChange={(e) => setEdRole(e.target.value as Role)}>
                                {ROLE_OPTIONS.map((r) => (
                                  <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="field-label" htmlFor={`ed-pw-${rowId}`}>新しいパスワード</label>
                              <input
                                id={`ed-pw-${rowId}`}
                                className="input"
                                type="text"
                                autoComplete="off"
                                value={edPw}
                                onChange={(e) => setEdPw(e.target.value)}
                                placeholder="変更しない場合は空欄"
                              />
                            </div>
                          </div>
                          <div className="row" style={{ gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                            <Btn variant="primary" onClick={() => void saveEdit(m)} disabled={edBusy}>
                              {edBusy ? "保存中…" : "保存する"}
                            </Btn>
                            <Btn variant="ghost" onClick={() => setEditFor(null)} disabled={edBusy}>
                              キャンセル
                            </Btn>
                          </div>
                          {edMsg ? (
                            <p className="jp" role="alert" style={{ margin: "10px 0 0", fontSize: 13, color: "var(--danger)" }}>
                              {edMsg}
                            </p>
                          ) : (
                            <p className="jp" style={{ margin: "10px 0 0", fontSize: 12, color: "var(--muted)" }}>
                              ロール変更は、このメンバーの次回ログインから反映されます。パスワードを設定するとメール＋パスワードでログインできます。
                            </p>
                          )}
                        </div>
                      ) : null}
                    </Fragment>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </ConsoleShell>
  );
}

const mcols = { gridTemplateColumns: "46px 1fr 130px minmax(0,1fr) auto" } as const;
